import { HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector, untracked } from '@angular/core';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { ApiError, errorStatus } from '../../data/api/api-error';
import { AuthApi } from '../../data/api/auth.api';
import { isApiRequest } from '../../data/http/api-envelope';
import { AUTH_PATHS } from '../../data/http/auth-http.api';
import { AuthStore } from '../auth-store';
import { TokenStore } from '../token-store';

// Общий полёт на весь модуль: N параллельных 401 обязаны дождаться ОДНОГО обновления, а не слать N штук.
let refreshInFlight: Observable<string> | null = null;

function sharedRefresh(
  api: AuthApi,
  tokens: TokenStore,
  auth: InstanceType<typeof AuthStore>,
  refreshToken: string,
): Observable<string> {
  const flight =
    refreshInFlight ??
    api.refresh(refreshToken).pipe(
      // Сервер ротирует refresh: старый умирает сразу, поэтому сохраняются ОБА новых токена.
      // Гейт по памяти и storage сужает гонку вкладок, но не закрывает: копия storage может отставать.
      tap((pair) => {
        if (untracked(tokens.refreshToken) === refreshToken && tokens.storageHolds(refreshToken)) {
          tokens.set(pair);
        }
      }),
      map((pair) => pair.token),
      // В самом полёте, а не у подписчика: storage сверяем с токеном, который отверг сервер.
      catchError((failure: unknown) => {
        const status = errorStatus(failure);
        // Сессию гасим ТОЛЬКО когда её отверг сервер: на сетевом сбое и 5xx выкидывать из аккаунта нельзя.
        if (status !== 400 && status !== 401) {
          return throwError(() => failure);
        }
        // Другой номер — только перезагрузка: его пару не берём и его сессию из storage не стираем.
        if (auth.reloadIfAccountChanged()) {
          return throwError(() => new ApiError(401, 'unauthorized'));
        }
        // Наш refresh могла сжечь соседняя вкладка: её свежую пару берём, а не стираем из общего storage.
        const stored = tokens.adoptStored();
        if (stored !== null && stored.refreshToken !== refreshToken) {
          return of(stored.token);
        }
        tokens.clear();
        return throwError(() => new ApiError(401, 'unauthorized'));
      }),
      // Сброс и по ошибке тоже: залипший мёртвый полёт не дал бы обновиться следующему входу.
      finalize(() => {
        refreshInFlight = null;
      }),
      // refCount: false — отписка первого запроса не должна отменять обновление для остальных.
      shareReplay({ bufferSize: 1, refCount: false }),
    );

  refreshInFlight = flight;
  return flight;
}

/** Входы (SMS, Telegram), привязка и сам refresh исключены: 401 там — отказ, а не протухший access. */
function isAuthEntryPoint(url: string): boolean {
  return (
    url.startsWith(AUTH_PATHS.refresh) ||
    url.startsWith(AUTH_PATHS.code) ||
    url.startsWith(AUTH_PATHS.verify) ||
    // Префикс покрывает и `/telegram/link`.
    url.startsWith(AUTH_PATHS.telegram) ||
    // Выход тоже сюда: 401 от старого /logout иначе обновил бы токены уже другой сессии.
    url.startsWith(AUTH_PATHS.logout)
  );
}

/** Навигации на /login здесь нет намеренно: роутер — забота гуарда и стора сессии. */
export const refreshTokenInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req.url) || isAuthEntryPoint(req.url)) {
    return next(req);
  }

  const tokens = inject(TokenStore);
  const api = inject(AuthApi);
  // AuthStore — только на 401: запросы из APP_INITIALIZER подняли бы его вместе со сторами адресов и корзины.
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((error: unknown) => {
      // Токен читается в момент падения, а не запроса: соседний 401 мог уже принести новую пару.
      const refreshToken = untracked(tokens.refreshToken);
      if (errorStatus(error) !== 401 || refreshToken === null) {
        return throwError(() => error);
      }
      const auth = injector.get(AuthStore);
      // Вход другим номером мог пройти мимо усыплённой вкладки: refresh за прежний аккаунт уже не нужен.
      if (auth.reloadIfAccountChanged()) {
        return throwError(() => error);
      }

      return sharedRefresh(api, tokens, auth, refreshToken).pipe(
        // next() ведёт вниз по цепочке, а не в её начало: повтор в эту ветку уже не попадёт.
        // Удачный refresh аккаунт не сверяет: после входа другого номера запрос прежнего экрана не повторяем.
        switchMap((token) =>
          auth.reloadIfAccountChanged()
            ? throwError(() => error)
            : next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })),
        ),
      );
    }),
  );
};
